import cn from "classnames";
import { AccessPermitsList } from "components/shared/accessPermitsList";
import BookIndex from "components/shared/book/list";
import BookItemStatus from "components/shared/book/statusCell";
import DropdownBtn from "components/shared/dropdown";
import InlineBtn from "components/shared/inlineBtn";
import SharedReactTable from "components/shared/reactTable";
import { defaultMultipleSelectProps, ValueContainer } from "components/shared/select";
import { dateTimeToView } from "helpers/date";
import { accessPermitRelationTypesEnum, featureModuleTypesEnum } from "helpers/enums";
import { paths } from "helpers/paths";
import {
    BooleanColumnFilter,
    DateColumnFilter,
    EnumColumnFilter,
    isDeletedColumn,
    NumericColumnFilter,
    rowIndexColumn,
    SelectColumnFilter,
} from "helpers/reactTable";
import { tableContainer } from "helpers/redux";
import { enumLabel } from "helpers/system";
import policy from "policies";
import Tooltip from "rc-tooltip";
import React from "react";
import { Link } from "react-router-dom";
import { selectList as accessPermitsList, getInfoList } from "sources/books/accessPermits";
import { list as mechanicsList } from "sources/books/mechanics";
import { deviceNumbersList as trackersList } from "sources/books/trackers";
import {
    addAllTrailersToRefuelingSyncList,
    addTrailersToRefuelingSyncList,
    removeAllTrailersFromRefuelingSyncList,
    removeTrailersFromRefuelingSyncList,
    setResourceActivity,
} from "sources/books/trailers";
import { list as transportColumnsList } from "sources/books/transportColumns";

class TrailersList extends BookIndex {
    state = { selectedItems: [], tableColumns: this.tableColumns, accessLoaded: false, accessPermits: [] };

    get tableColumns() {
        const checkboxColumn = {
            Header: () => {
                const allItemsSelected = this.state.selectedItems.length === this.props.items.length;
                return (
                    <div className="d-flex justify-content-center pt-1">
                        <span
                            className={cn("checkbox", { "checkbox--checked": allItemsSelected })}
                            onClick={() =>
                                allItemsSelected
                                    ? this.setState({ selectedItems: [] })
                                    : this.setState({ selectedItems: this.props.items })
                            }
                        />
                    </div>
                );
            },
            accessor: "checkbox",
            width: 30,
            sortable: false,
            Cell: ({ row: { original } }) => {
                const itemSelected = this.state.selectedItems.some((item) => item.id === original.id);
                return (
                    <div className="d-flex justify-content-center pt-1">
                        <span
                            className={cn("checkbox", { "checkbox--checked": itemSelected })}
                            onClick={() =>
                                itemSelected
                                    ? this.setState({
                                          selectedItems: this.state.selectedItems.filter(
                                              (item) => item.id !== original.id,
                                          ),
                                      })
                                    : this.setState({ selectedItems: this.state.selectedItems.concat(original) })
                            }
                        />
                    </div>
                );
            },
        };
        const refuelingInfoColumns = [
            {
                Header: "Объем бака",
                id: "fuelTank/totalVolume",
                accessor: (d) => d.fuelTank?.totalVolume,
                filterable: true,
                filterType: "numeric",
                Filter: NumericColumnFilter,
            },
            {
                Header: "Расход топлива л/100 км.",
                id: "fuelTank/fuelConsumption",
                accessor: (d) => d.fuelTank?.fuelConsumption,
                filterable: true,
                filterType: "numeric",
                Filter: NumericColumnFilter,
            },
            {
                Header: "Последнее обновление ДУТ",
                accessor: "lastDieselSensorValueFixedAt",
                sortable: false,
                filterable: true,
                filterType: "date",
                Filter: DateColumnFilter,
                Cell: ({ value }) => dateTimeToView(value, "DD.MM.YYYY HH:mm:ss"),
                CSVExport: ({ value }) => dateTimeToView(value, "DD.MM.YYYY HH:mm:ss"),
            },
            {
                Header: "Участвует в планировании АЗС",
                accessor: "isInRefuelingSyncList",
                sortable: false,
                filterable: true,
                filterType: "boolean",
                Filter: (props) => {
                    const options = [
                        { label: "Показать все", value: "all" },
                        { label: "Да", value: "true" },
                        { label: "Нет", value: "false" },
                    ];
                    return <BooleanColumnFilter {...props} options={options} />;
                },
                Cell: ({ value }) => <div className="text-center">{value ? "Да" : "Нет"}</div>,
                CSVExport: ({ value }) => (value ? "Да" : "Нет"),
            },
        ];
        const { isTruckingOrganizationAdmin } = this.props.roles;
        const { availableFeatureModules } = this.props.profile;
        const { refuelingSettings } = this.props;
        const refuelingEnabled =
            availableFeatureModules.includes(featureModuleTypesEnum.RefuelingPlanning) &&
            !refuelingSettings.hideRefuelingPlanning;
        const syncRefuelingEnabled = isTruckingOrganizationAdmin && refuelingEnabled;
        return [
            ...(syncRefuelingEnabled ? [checkboxColumn] : []),
            rowIndexColumn,
            { Header: "ID", accessor: "id", width: 100, filterable: true, filterType: "int" },
            {
                Header: "Номер",
                accessor: "number",
                filterable: true,
                Cell: ({
                    value,
                    row: {
                        original: { id, isInRefuelingSyncList },
                    },
                }) => (
                    <>
                        <Link to={paths.trailersShow(id)}>{value}</Link>
                        {isInRefuelingSyncList && !refuelingSettings.hideRefuelingPlanning && (
                            <Tooltip overlay="Участвует в планировании АЗС">
                                <i className="icon-uEA36-azs pl-2 text-success align-middle" />
                            </Tooltip>
                        )}
                    </>
                ),
            },
            {
                Header: "Статус",
                accessor: "isActive",
                sortable: true,
                filterable: true,
                filterType: "boolean",
                Filter: (props) => (
                    <BooleanColumnFilter
                        {...props}
                        options={[
                            { label: "Показать все", value: "all" },
                            { label: "Активные", value: "true" },
                            { label: "Неактивные", value: "false" },
                        ]}
                    />
                ),
                Cell: ({ value, row: { original } }) => (
                    <BookItemStatus
                        callback={() => this.props.mergeParams({})}
                        modal={this.props.modal}
                        title={`Изменения статуса прицепа ${original.number}`}
                        initialData={{ trailerId: original.id }}
                        isActive={value}
                        resourceInactivityInfo={original.resourceInactivityInfo}
                        setResourceActivity={setResourceActivity}
                        canChangeStatus={!policy("user", { user: { roles: this.props.roles } }).isOnlyExtendedViewer()}
                    />
                ),
            },
            {
                Header: "Тип",
                accessor: "typeId",
                filterable: true,
                filterType: "enum",
                Filter: (props) => <EnumColumnFilter {...props} filterEnum={this.props.enums.trailerType} />,
                Cell: ({ value }) => enumLabel(this.props.enums.trailerType, value, "Не указано"),
                CSVExport: ({ value }) => enumLabel(this.props.enums.trailerType, value, "Не указано"),
            },
            {
                Header: "Тип загрузки/выгрузки",
                accessor: "loadUnloadOptions",
                id: "loadUnloadOptions",
                filterable: true,
                filterType: "select",
                sortable: false,
                isMulti: true,
                Filter: (props) => (
                    <SelectColumnFilter
                        {...props}
                        selectProps={{
                            isMulti: true,
                            options: this.props.enums.loadUnloadType,
                            inputProps: { name: "loadUnloadOptions" },
                        }}
                    />
                ),
                Cell: ({ value }) =>
                    value.map(({ id }) => enumLabel(this.props.enums.loadUnloadType, id, "Не указано")).join(", "),
                CSVExport: ({ value }) =>
                    value.map(({ id }) => enumLabel(this.props.enums.loadUnloadType, id, "Не указано")).join(", "),
            },
            {
                Header: "Допуски",
                accessor: "accessPermitIds",
                sortable: false,
                filterable: true,
                filterType: "arrayContains",
                Filter: (props) => (
                    <SelectColumnFilter
                        {...props}
                        selectProps={{
                            isMulti: true,
                            url: (props) =>
                                accessPermitsList({
                                    filter: { relationType: accessPermitRelationTypesEnum.Trailer },
                                    ...props,
                                }),
                            inputProps: { name: "accessPermitIds" },
                            propName: "name",
                            components: { ValueContainer },
                            ...defaultMultipleSelectProps,
                        }}
                    />
                ),
                Cell: ({ value }) => <AccessPermitsList ids={value} items={this.state.accessPermits} />,
            },
            {
                Header: "Трекер",
                accessor: "trackerDeviceNumber",
                id: "trackerId",
                filterable: true,
                sortKey: "trackerId",
                filterType: "select",
                Filter: (props) => (
                    <SelectColumnFilter
                        {...props}
                        selectProps={{ url: trackersList, inputProps: { name: "trackerId" }, propName: "deviceNumber" }}
                    />
                ),
            },
            {
                Header: "Механик",
                accessor: "mechanic",
                id: "mechanicId",
                filterable: true,
                filterType: "inArray",
                isMulti: true,
                sortable: false,
                Filter: (props) => (
                    <SelectColumnFilter
                        {...props}
                        selectProps={{
                            isMulti: true,
                            url: mechanicsList,
                            inputProps: { name: "mechanic" },
                            propName: "name",
                            components: { ValueContainer },
                            ...defaultMultipleSelectProps,
                        }}
                    />
                ),
                Cell: ({ value }) => (value ? value.name : ""),
                CSVExport: ({ value }) => (value ? value.name : ""),
            },
            {
                Header: "Колонна",
                accessor: "transportColumn",
                id: "transportColumnId",
                filterable: true,
                filterType: "select",
                Filter: (props) => (
                    <SelectColumnFilter
                        {...props}
                        selectProps={{
                            url: transportColumnsList,
                            inputProps: { name: "transportColumnId" },
                            propName: "name",
                        }}
                    />
                ),
                Cell: ({ value }) => (value ? value.name : ""),
                CSVExport: ({ value }) => (value ? value.name : ""),
            },
            ...(refuelingEnabled ? refuelingInfoColumns : []),
            isDeletedColumn(this.restore, this.props.canEditBooks),
        ];
    }

    componentDidMount() {
        getInfoList({ preparedFilter: { relationType: accessPermitRelationTypesEnum.Trailer } }).then((res) =>
            this.setState({ accessPermits: res?.data, accessLoaded: true }),
        );
    }

    componentDidUpdate(prevProps, prevState) {
        if (
            prevProps.refuelingSettings?.hideRefuelingPlanning !==
                this.props.refuelingSettings?.hideRefuelingPlanning ||
            prevProps.canEditBooks !== this.props.canEditBooks ||
            prevState.accessLoaded !== this.state.accessLoaded
        ) {
            this.setState({ tableColumns: this.tableColumns });
        }
    }

    renderActions = () => {
        const { mergeParams } = this.props;
        const actions = [{ value: "Обновить", cname: "refresh", active: true, action: mergeParams, params: {} }];
        if (this.state.selectedItems.some((item) => item.isInRefuelingSyncList)) {
            actions.unshift({
                value: "Удалить прицепы из списка синхронизации заправок",
                cname: "delete",
                active: true,
                action: this.removeItemsFromSyncList,
                params: {},
            });
        }
        if (this.state.selectedItems.some((item) => !item.isInRefuelingSyncList)) {
            actions.unshift({
                value: "Добавить прицепы в список синхронизации заправок",
                cname: "plus",
                active: true,
                action: this.addItemsToSyncList,
                params: {},
            });
        }
        return actions.map((item, index) => <InlineBtn key={index} {...item} />);
    };

    addItemsToSyncList = () => {
        this.props.modal({
            name: "confirm",
            title: "Добавить прицепы в список синхронизации заправок?",
            buttons: { confirm: "Да", cancel: "Нет" },
            onSuccessAction: () => {
                this.setState({ selectedItems: [] });
                this.props.mergeParams({});
            },
            action: () =>
                addTrailersToRefuelingSyncList({
                    trailerIds: this.state.selectedItems.reduce(
                        (acc, item) => (!item.isInRefuelingSyncList ? acc.concat(item.id) : acc),
                        [],
                    ),
                }),
        });
    };

    removeItemsFromSyncList = () => {
        this.props.modal({
            name: "confirm",
            title: "Удалить прицепы из списка синхронизации заправок?",
            buttons: { confirm: "Да", cancel: "Нет" },
            onSuccessAction: () => {
                this.setState({ selectedItems: [] });
                this.props.mergeParams({});
            },
            action: () =>
                removeTrailersFromRefuelingSyncList({
                    trailerIds: this.state.selectedItems.reduce(
                        (acc, item) => (item.isInRefuelingSyncList ? acc.concat(item.id) : acc),
                        [],
                    ),
                }),
        });
    };

    renderHead = () => {
        const { canCreateBooks } = this.props;
        return (
            <div className="book-list__head">
                <div className="book-list__head-close close close--sm" onClick={this.close} />
                <div className="book-list__head-title">
                    {this.props.title}
                    {this.renderCounter()}
                </div>
                <div className="d-flex align-items-center">
                    {canCreateBooks && this.renderCreateButton()}
                    {this.renderMoreBtn()}
                </div>
            </div>
        );
    };

    renderMoreBtn = () => {
        const { roles, refuelingSettings } = this.props;
        const { availableFeatureModules } = this.props.profile;
        const syncRefuelingEnabled =
            roles.isTruckingOrganizationAdmin &&
            availableFeatureModules.includes(featureModuleTypesEnum.RefuelingPlanning) &&
            !refuelingSettings.hideRefuelingPlanning;
        const btns = [];
        if (syncRefuelingEnabled) {
            btns.push(
                {
                    text: "Добавление всех машин в список синхронизации заправок",
                    action: () =>
                        this.props.modal({
                            name: "confirm",
                            title: "Добавить все прицепы в список синхронизации заправок?",
                            buttons: { confirm: "Да", cancel: "Нет" },
                            onSuccessAction: () => {
                                this.setState({ selectedItems: [] });
                                this.props.mergeParams({});
                            },
                            action: addAllTrailersToRefuelingSyncList,
                        }),
                },
                {
                    text: "Удаление всех машин из списка синхронизации заправок",
                    action: () =>
                        this.props.modal({
                            name: "confirm",
                            title: "Удалить все прицепы из списка синхронизации заправок?",
                            buttons: { confirm: "Да", cancel: "Нет" },
                            onSuccessAction: () => {
                                this.setState({ selectedItems: [] });
                                this.props.mergeParams({});
                            },
                            action: removeAllTrailersFromRefuelingSyncList,
                        }),
                },
            );
        }
        if (!btns.length) {
            return null;
        }

        if (btns.length === 1) {
            return (
                <div className="btn btn-sm btn-secondary ml-2" onClick={btns[0].action}>
                    {btns[0].text}
                </div>
            );
        }

        const btnProps = {
            text: "Еще",
            cname: "ml-2 btn btn-sm btn-secondary",
            align: "end",
            wrapCname: "b-timeline-point__settings--wrap",
            items: btns,
        };
        return <DropdownBtn {...btnProps} />;
    };

    renderCreateButton = () => {
        const { modal, mergeParams, canEditBooks, roles, profile, refuelingSettings } = this.props;
        const action = () =>
            modal({
                name: "createTrailer",
                title: "Создание прицепа",
                windowMod: "wide",
                canEditBooks,
                roles,
                profile,
                mergeParams,
                refuelingSettings,
            });
        return (
            <a className="btn btn-brand btn-sm" onClick={action}>
                Создать прицеп
            </a>
        );
    };

    renderListComponent = () => {
        return <SharedReactTable {...this.props} columns={this.state.tableColumns} />;
    };
}

export default tableContainer(TrailersList, "trailers");
